import {Button, Modal} from "react-bootstrap";
import {t} from "ttag";
import {useCallback, useId, useState} from "react";
import { createRoot } from "react-dom/client";

type UploadFilter = {
    regex: string,
    error: string
}

export const fileImageFilter: UploadFilter = {
    regex: "image/.*",
    error: "File must be an image"
}

export const filePdfFilter: UploadFilter = {
    regex: "application/pdf",
    error: "File must be a PDF"
}

function getFileType(file: Blob | File) {
    if (file.type) {
        return file.type;
    }
    if (file instanceof File) {
        let reg = /(?:\.([^.]+))?$/.exec(file.name)
        if (reg && reg.length >= 1) {
            return reg[1];
        }
    }
    return ''; // this is already the fallback
}

export async function uploadFile(file: Blob | File, typeFilter : UploadFilter | null = null): Promise<{ id: string, previewUrl: string } | { error: string }> {
    if(typeFilter) {
        if(!getFileType(file).match(typeFilter.regex)) {
            return { error: typeFilter.error }
        }
    }
    const baseUpload = await fetch(`/uploads`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `filename=${encodeURIComponent(file instanceof File ? file.name : "blob")}&contentType=${encodeURIComponent(file.type)}` +
            `&byteCount=${file.size}`,
        credentials: 'same-origin'
    })
    if(!baseUpload.ok) {
        return { error: await baseUpload.text() }
    }
    const uploadInfo : { uploadId: string, target: string, previewUrl: string } = await baseUpload.json()

    const response = await fetch(uploadInfo.target, {
        method: 'PUT',
        headers: {
            'Content-Type': file.type,
        },
        body: file,
        credentials: 'same-origin'
    })
    if(!response.ok) {
        return { error: await response.text() }
    }

    return { id: uploadInfo.uploadId, previewUrl: uploadInfo.previewUrl }
}

export interface FileData { id: string, name: string, type: string, previewUrl: string, file: File }

function FileUploadDialog({onComplete, filter, label, text, uploadText, doUpload} : {
    onComplete: (file?: FileData) => void | Promise<void>,
    filter?: UploadFilter,
    label: string,
    text: string,
    uploadText: string,
    doUpload: boolean
}) {
    const elementId = useId();
    const [value, setValue] = useState<FileList | null>(null);
    const [errorText, setErrorText] = useState("");

    const submitFile = useCallback(async () => {
        if(!value || value.length < 1) {
            onComplete(undefined)
            return;
        }
        const file = value[0];
        // If the fileType isn't valid, fall back on the file extension
        const fileType = getFileType(file);
        if (doUpload) {
            let uploadData = await uploadFile(file, filter);
            if ('error' in uploadData) {
                setErrorText(uploadData.error);
                throw new Error(uploadData.error);
            } else {
                setErrorText("")
                try {
                    await onComplete({id: uploadData.id!, previewUrl: uploadData.previewUrl!, name: file.name, type: fileType, file});
                } catch (e) {
                    let message = 'Unknown Error'
                    if (e instanceof Error) message = e.message
                    setErrorText(message)
                }
            }
        } else {
            setErrorText("")
            try {
                await onComplete({id: '', previewUrl: '', name: file.name, type: fileType, file});
            } catch (e) {
                let message = 'Unknown Error'
                if (e instanceof Error) message = e.message
                setErrorText(message)
            }
        }
    }, [value])

    return <Modal show={true} onHide={() => onComplete(undefined)}>
    <Modal.Header closeButton>
    <Modal.Title>{label}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
        <form onSubmit={() => { submitFile(); return false; }}>
            <div>
                <label htmlFor={elementId}>{text}</label>
                <input type="file" className="form-control" id={elementId} accept={filter?.regex} onChange={(e) => setValue(e.target.files)} />
                {errorText}
            </div>
        </form>
    </Modal.Body>
    <Modal.Footer>
        <Button variant="secondary" onClick={() => onComplete(undefined)}>
        {t`Close`}
    </Button>
    <Button variant="primary" onClick={() => submitFile()}>
        {uploadText}
    </Button>
    </Modal.Footer>
    </Modal>
}

export function fileUploadDialog({label, text, buttonText = t`Upload`, onComplete, doUpload = true, filter} : {
    label: string,
    text: string,
    buttonText?: string,
    onComplete?: (file?: FileData) => void | Promise<void>,
    doUpload?: boolean
    filter?: UploadFilter
}) {
    let resolver : (file?: FileData) => void
    const returnedPromise = new Promise<FileData | undefined>(resolve => {
        resolver = resolve
    })

    const container = document.createElement("div");
    const root = createRoot(container);
    async function onCompleteWrapper(file?: FileData) {
        if(file && onComplete) {
            await onComplete(file);
        }
        root.unmount();
        container.remove();
        resolver(file)
    }

    root.render(<FileUploadDialog onComplete={onCompleteWrapper} label={label} filter={filter} text={text} uploadText={buttonText} doUpload={doUpload} />);
    return returnedPromise;
}